import { useTranslation } from 'react-i18next';
import { RobotViewModel } from '../../../RobotViewModel';
import { StyledRobotCleaningRoute } from './RobotCleaningRoute.styles';
import { Machine } from 'app/modules/machine-inventory/interfaces/Machine.types';
import {
  MachineConnectionStatus,
  RobotStatus,
} from 'app/cross-cutting-concerns/communication/interfaces/am-api-graphql';
import { RobotUtils } from 'app/utils/robot/RobotUtils';
import { useCv50CleaningProgressPercent } from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/robotCleaningProgress/hooks/useCv50CleaningProgressPercent';

export type RobotClassification = 'B50' | 'CV50';

interface RobotCleaningRouteProps {
  robot: Machine;
  showProgressPercent?: boolean;
  /**
   * CV50 doesn't emit `repeatProgress` telemetry, so for CV50 the name comes from the robot's
   * `map` state instead. Left unset at the fleet-dashboard call sites, which must keep their
   * existing telemetry-based name for every model.
   */
  robotClassification?: RobotClassification;
}

export const RobotCleaningRoute = ({
  robot,
  showProgressPercent = false,
  robotClassification,
}: RobotCleaningRouteProps): JSX.Element => {
  const { t } = useTranslation();
  const robotModelView = new RobotViewModel(robot);

  const isRobotCleaning =
    robot.robotStatus === RobotStatus.Autonomous && robot.connectionStatus === MachineConnectionStatus.Online;

  const routeName =
    robotClassification === 'CV50'
      ? RobotUtils.getRobotMapName(robot, t)
      : robotModelView.getRobotCleaningRouteNameAndDate.routeName || t('common.NA');

  const percent = useCv50CleaningProgressPercent(
    robot,
    isRobotCleaning,
    robotModelView.getRepeatPathProgressData.progress
  );

  if (showProgressPercent && isRobotCleaning) {
    return (
      <StyledRobotCleaningRoute className="robot-cleaning-route--header">
        <div className="robot-cleaning-route__header">
          <span className="robot-cleaning-route__details robot-cleaning-route__details--header">{routeName}</span>
          <span className="robot-cleaning-route__percent">
            {t('robotDashboard.cleaningRoute.label.percentComplete', { percent })}
          </span>
        </div>
      </StyledRobotCleaningRoute>
    );
  }

  return (
    <StyledRobotCleaningRoute>
      {!isRobotCleaning && (
        <p className="robot-cleaning-route__label">{t(robotModelView.getCleaningRouteLabel)}&nbsp;</p>
      )}
      <span className="robot-cleaning-route__details">{routeName}</span>
    </StyledRobotCleaningRoute>
  );
};
